import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Info, Paperclip, StickyNote, X, Trash2, Pencil } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import LinkedNoteDialog from '../farmfolio/LinkedNoteDialog';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import { useDateInput } from '../../hooks/useDateInput';
import { useConfirm } from '../../components/common/ConfirmDialog';

const EVENT_TYPES = [
  { value: 'tease_score',          label: 'Tease Score' },
  { value: 'palpation',            label: 'Palpation Record' },
  { value: 'ovulation_confirmed',  label: 'Ovulation Confirmed' },
  { value: 'bred',                 label: 'Bred' },
  { value: 'pregnant',             label: 'Pregnant' },
];

const TEASE_DESCRIPTIONS = [
  { score: 1, title: 'Diestrus / Out of Heat', desc: 'The mare shows obvious hostility toward the stallion. She may pin her ears back, strike, bite, kick, and squeal.' },
  { score: 2, title: 'Indifference / Passive',  desc: 'The mare ignores the stallion entirely or shows passive, unbothered behavior.' },
  { score: 3, title: 'Slight Interest',          desc: 'The mare shows minor interest in the teaser. She may exhibit slight tail raising and winking.' },
  { score: 4, title: 'Obvious Interest',         desc: 'The mare is highly receptive. She stands passively, raises her tail, and frequently winks and urinates.' },
  { score: 5, title: 'Intense / Strong Heat',    desc: 'The mare displays peak readiness to breed. She will eagerly turn her hindquarters to the stallion, squatting, urinating frequently, and showing maximum vulvar winking.' },
];

const FC_DESCRIPTIONS = [
  { code: 'T', label: 'Turgid' },
  { code: 'S', label: 'Soft or breakable' },
  { code: 'O', label: 'Ovulated' },
];

const CERVIX_SIZES = ['<10 mm', '≥10 mm - <30 mm', '≥30 mm'];

const today = new Date().toLocaleDateString('sv');
const nowTime = new Date().toTimeString().slice(0, 5);

function InfoTooltip({ children }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground cursor-pointer inline ml-1" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed" side="right">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AddBreedingEventModal({ open, onClose, onSaved, seasonId, horseId, initialType, initialDate, tileDate, editing, onBredSelected, isFarm, horseSoldInfo }) {
  const fileRef = useRef();
  const [eventType, setEventType]       = useState(initialType || 'tease_score');
  const [eventDate, setEventDate]       = useState(today);
  const [eventTime, setEventTime]       = useState(nowTime);
  const [notes, setNotes]               = useState('');
  // Tease
  const [score, setScore]               = useState('');
  // Palpation — per-ovary data
  const [rightOvary, setRightOvary] = useState({ follicle_size: '', follicular_consistency: '' });
  const [leftOvary, setLeftOvary]   = useState({ follicle_size: '', follicular_consistency: '' });
  const [cervixSize, setCervixSize] = useState('');
  // Attachments (palpation)
  const [attachments, setAttachments]           = useState([]);
  const [uploading, setUploading]               = useState(false);
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [showNote, setShowNote]                 = useState(false);
  const [noteRef, setNoteRef]                   = useState('');
  const [saving, setSaving]                     = useState(false);
  // Item 1: Estimated due date (pregnant events only)
  const [estimatedDueDate, setEstimatedDueDate] = useState('');
  const confirm = useConfirm();
  const estDueDateRef = useDateInput(estimatedDueDate);
  // Item 3: Sold dialog
  const [soldDialogOpen, setSoldDialogOpen]     = useState(false);
  const [soldDate, setSoldDate]                 = useState(today);
  const soldDateRef = useDateInput(soldDate);
  // Issue 4: Keep in care? (FarmFolio tenants only)
  const [keepInCareOpen, setKeepInCareOpen]     = useState(false);
  // Edit pregnancy date toggle (pencil icon)
  const [editingPregDate, setEditingPregDate]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setEventType(initialType || editing?.event_type || 'tease_score');
    setEventDate(editing?.event_date || initialDate || today);
    setEventTime(editing?.event_time || nowTime);
    setNotes(editing?.notes || '');
    setAttachments([]);
    setShowNote(false);
    const d = editing?.data || {};
    setScore(d.score ? String(d.score) : '');
    setRightOvary({ follicle_size: d.right_ovary?.follicle_size || '', follicular_consistency: d.right_ovary?.follicular_consistency || '' });
    setLeftOvary({ follicle_size: d.left_ovary?.follicle_size || '', follicular_consistency: d.left_ovary?.follicular_consistency || '' });
    setCervixSize(d.cervix_size || '');
    setNoteRef(`Breeding event — ${editing?.event_date || today}`);
    // Item 1: init estimated due date
    const existingDue = (editing?.data || {}).estimated_due_date;
    if (existingDue) {
      setEstimatedDueDate(existingDue);
    } else if ((initialType || editing?.event_type) === 'pregnant') {
      // Use the clicked/event date (not today) as the base for +340 days
      const base = new Date((editing?.event_date || initialDate || today) + 'T00:00:00');
      base.setDate(base.getDate() + 340);
      setEstimatedDueDate(base.toLocaleDateString('sv'));
    } else {
      setEstimatedDueDate('');
    }
    // Item 3: reset sold state
    setSoldDialogOpen(false);
    setSoldDate(today);
    setEditingPregDate(false);
  }, [open, editing, initialType, initialDate]);

  const buildData = () => {
    if (eventType === 'tease_score') return { score: parseInt(score) || 0 };
    if (eventType === 'palpation')   return { right_ovary: rightOvary, left_ovary: leftOvary, cervix_size: cervixSize, has_attachments: attachments.length > 0, has_note: editing?.data?.has_note || false };
    if (eventType === 'pregnant')    return { ...(editing?.data || {}), estimated_due_date: estimatedDueDate };
    return {};
  };

  const handleDelete = async () => {
    if (!editing?.id) return;
    if (!await confirm({ title: 'Delete this event?', destructive: true })) return;
    try {
      await api.delete(`/breeding-seasons/events/${editing.id}`);
      onSaved(eventType);
      onClose();
    } catch { toast.error('Failed to delete'); }
  };

  const handleResolve = async (status, extraData = {}) => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      await api.put(`/breeding-seasons/events/${editing.id}`, {
        data: { ...(editing.data || {}), ...extraData },
        resolved_status: status,
      });
      onSaved(eventType);
      onClose();
    } catch { toast.error('Failed to update pregnancy status'); }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!eventDate) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      const payload = {
        season_id: seasonId, horse_id: horseId, event_type: eventType,
        event_date: eventDate, event_time: eventTime, notes,
        data: buildData(),
      };
      let res;
      if (editing) res = await api.put(`/breeding-seasons/events/${editing.id}`, payload);
      else         res = await api.post('/breeding-seasons/events', payload);

      const eventId = res.data?.id || editing?.id;

      if (attachments.length > 0 && eventId) {
        setUploading(true);
        await Promise.allSettled(attachments.map(({ file }) => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('horse_id', horseId);
          fd.append('description', `Palpation Record — ${eventDate}`);
          return api.post('/files/upload', fd);
        }));
        setUploading(false);
      }

      onSaved(eventType);
      onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const label = EVENT_TYPES.find(e => e.value === eventType)?.label || '';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${label}` : `Add ${label}`}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {!initialType && (
              <div className="space-y-1">
                <Label className="font-semibold">Event Type</Label>
                <Select value={eventType} onValueChange={v => {
                    if (v === 'bred' && onBredSelected) { onClose(); onBredSelected(); return; }
                    setEventType(v);
                    if (v === 'pregnant') {
                      const base = new Date((editing?.event_date || initialDate || today) + 'T00:00:00');
                      base.setDate(base.getDate() + 340);
                      setEstimatedDueDate(base.toLocaleDateString('sv'));
                    }
                  }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EVENT_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {/* LEFT: Pregnant Since (with pencil to edit) */}
              <div className="space-y-1">
                <Label className="font-semibold">{editing?.event_type === 'pregnant' ? 'Pregnant Since' : 'Date'}</Label>
                {editing?.event_type === 'pregnant' ? (
                  editingPregDate ? (
                    <div className="space-y-1">
                      <Input type="date" value={eventDate} onChange={e => {
                        setEventDate(e.target.value);
                        if (e.target.value) { const b = new Date(e.target.value + 'T00:00:00'); b.setDate(b.getDate() + 340); setEstimatedDueDate(b.toLocaleDateString('sv')); }
                      }} className="time-filled" />
                      <Input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} className={eventTime ? 'time-filled' : ''} />
                      <button className="text-xs text-muted-foreground underline" onClick={() => setEditingPregDate(false)}>Done</button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1 pt-1">
                      <p className="text-sm font-medium">
                        {new Date(eventDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {eventTime && (() => { const [h,m] = eventTime.split(':').map(Number); const ap = h>=12?'pm':'am'; const h12=h%12||12; return ` @ ${h12}:${String(m).padStart(2,'0')} ${ap}`; })()}
                      </p>
                      <button className="text-muted-foreground hover:text-primary mt-0.5" onClick={() => setEditingPregDate(true)} title="Edit date & time">
                        <Pencil className="w-3 h-3" />
                      </button>
                    </div>
                  )
                ) : (
                  <Input type="date" value={eventDate} onChange={e => {
                    setEventDate(e.target.value);
                    if (eventType === 'pregnant' && e.target.value) { const base = new Date(e.target.value + 'T00:00:00'); base.setDate(base.getDate() + 340); setEstimatedDueDate(base.toLocaleDateString('sv')); }
                  }} className="time-filled" />
                )}
              </div>
              {/* RIGHT: tile date + days counter OR time field */}
              <div className="space-y-1">
                {editing?.event_type === 'pregnant' && tileDate && tileDate !== eventDate ? (
                  <>
                    <Label className="font-semibold">Date</Label>
                    <p className="text-sm font-medium pt-1">
                      {new Date(tileDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    {(() => { const days = Math.floor((new Date(tileDate + 'T00:00:00') - new Date(eventDate + 'T00:00:00')) / 86400000) + 1; return <p className="text-xs text-pink-700 font-medium">Days Pregnant: {days}</p>; })()}
                  </>
                ) : editing?.event_type !== 'pregnant' ? (
                  <>
                    <Label className="font-semibold">Time</Label>
                    <Input type="time" value={eventTime} onChange={e => setEventTime(e.target.value)} className={eventTime ? 'time-filled' : ''} />
                  </>
                ) : null}
              </div>
            </div>
            {/* Days counter when opening pregnant event from its own tile (no separate tileDate) */}
            {editing?.event_type === 'pregnant' && !editingPregDate && (!tileDate || tileDate === eventDate) && (() => {
              const since = new Date(eventDate + 'T00:00:00');
              const now = new Date(); now.setHours(0,0,0,0);
              const days = Math.floor((now - since) / 86400000) + 1;
              return <p className="text-xs text-pink-700 font-medium">{days}d pregnant</p>;
            })()}

            {/* ── Tease Score ── */}
            {eventType === 'tease_score' && (
              <div className="space-y-1">
                <Label className="font-semibold flex items-center">
                  Score (1–5)
                  <InfoTooltip>
                    <div className="space-y-2">
                      {TEASE_DESCRIPTIONS.map(t => (
                        <div key={t.score}>
                          <span className="font-semibold">{t.score} — {t.title}:</span>{' '}
                          <span>{t.desc}</span>
                        </div>
                      ))}
                    </div>
                  </InfoTooltip>
                </Label>
                <Select value={score || '__none__'} onValueChange={v => setScore(v === '__none__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select score" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {['1','2','3','4','5'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Palpation ── */}
            {eventType === 'palpation' && (
              <div className="space-y-3">
                {/* Two ovary cards side by side */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Left Ovary',  state: leftOvary,  setter: setLeftOvary  },
                    { label: 'Right Ovary', state: rightOvary, setter: setRightOvary },
                  ].map(({ label, state, setter }) => (
                    <div key={label} className="border rounded p-2.5 space-y-2">
                      <p className="text-xs font-semibold">{label}</p>
                      <div className="space-y-1">
                        <Label className="text-xs">Follicle Size</Label>
                        <Select value={state.follicle_size || '__none__'}
                          onValueChange={v => setter(s => ({ ...s, follicle_size: v === '__none__' ? '' : v }))}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="mm" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {['≤20 mm','30 mm','40 mm','≥50 mm'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs flex items-center gap-1">
                          Consistency
                          <InfoTooltip>
                            <div className="space-y-1">
                              {FC_DESCRIPTIONS.map(f => (
                                <div key={f.code}><span className="font-semibold">{f.code}</span> — {f.label}</div>
                              ))}
                            </div>
                          </InfoTooltip>
                        </Label>
                        <Select value={state.follicular_consistency || '__none__'}
                          onValueChange={v => setter(s => ({ ...s, follicular_consistency: v === '__none__' ? '' : v }))}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="T/S/O" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {['T','S','O'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cervix */}
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Cervix Size</Label>
                  <Select value={cervixSize || '__none__'} onValueChange={v => setCervixSize(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {CERVIX_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Attachments + Note (palpation only) */}
                <div className="space-y-2 pt-1">
                  <Button type="button" variant="outline" className="w-full justify-start gap-2 h-8 text-xs"
                    onClick={() => setAttachPickerOpen(true)}>
                    <Paperclip className="w-3.5 h-3.5" /> Upload Attachments
                  </Button>
                  {attachments.length > 0 && (
                    <div className="space-y-1">
                      {attachments.map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-muted/30 rounded px-2 py-1">
                          <span className="truncate">{a.name}</span>
                          <Button variant="ghost" size="icon" className="h-4 w-4 shrink-0"
                            onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button type="button" variant="outline" className="w-full justify-start gap-2 h-8 text-xs"
                    onClick={() => setShowNote(true)}>
                    <StickyNote className="w-3.5 h-3.5" /> Create Linked Note
                  </Button>
                </div>
              </div>
            )}

            {/* ── Notes (all types) ── */}
            <div className="space-y-1">
              <Label className="font-semibold">Notes</Label>
              <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
            </div>

            {/* ── Estimated Due Date (pregnant only) — Item 1 ── */}
            {eventType === 'pregnant' && (
              <div className="space-y-1">
                <Label className="font-semibold">Est. Due Date</Label>
                <Input ref={estDueDateRef} type="date" value={estimatedDueDate}
                  onChange={e => setEstimatedDueDate(e.target.value)} />
                <p className="text-xs text-muted-foreground">Defaults to 340 days from event date (standard equine gestation).</p>
              </div>
            )}

            {/* ── Covering Sire (bred only) — now handled by CoverRecordDialog via onBredSelected ── */}
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div>
              {editing && (
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={handleDelete} title="Delete event">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {/* Sold state — show when horse is sold (derived from horse record, not pregnancy event) */}
              {horseSoldInfo?.is_sold && (
                <div className="px-3 py-2 rounded bg-gray-100 border border-gray-200 text-sm text-gray-700">
                  {horseSoldInfo.sold_date
                    ? `Sold on ${new Date(horseSoldInfo.sold_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. Pregnancy records paused.`
                    : 'Horse has been sold. Pregnancy records paused.'}
                  {horseSoldInfo.keep_in_care && <span className="ml-2 text-green-700 font-medium">(Keeping in care)</span>}
                </div>
              )}
              {/* Slipped / Aborted / Sold — only on an active pregnant event, and only when horse not already sold */}
              {editing?.event_type === 'pregnant' && !editing?.resolved_status && !horseSoldInfo?.is_sold && (
                <>
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                    disabled={saving}
                    onClick={() => handleResolve('Slipped')}>
                    Slipped
                  </Button>
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                    disabled={saving}
                    onClick={() => handleResolve('Aborted')}>
                    Aborted
                  </Button>
                  {/* Item 3: Sold — opens keep-in-care check for FarmFolio tenants, then date dialog */}
                  <Button size="sm" variant="outline"
                    className="h-7 text-xs border-gray-400 text-gray-700 hover:bg-gray-50"
                    disabled={saving}
                    onClick={async () => {
                      setSoldDate(today);
                      if (isFarm) { setKeepInCareOpen(true); }
                      else { setSoldDialogOpen(true); }
                    }}>
                    Sold
                  </Button>
                </>
              )}
              {editing?.resolved_status && (
                <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs text-gray-700 font-medium self-center">
                  {editing.resolved_status === 'Sold' && editing.data?.sold_date
                    ? `Sold as of ${new Date(editing.data.sold_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. No further records available.`
                    : `Resolved: ${editing.resolved_status}`}
                </div>
              )}
              <Button variant="outline" onClick={onClose} disabled={saving || uploading}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || uploading}
                className="bg-primary hover:bg-primary/90 text-white">
                {saving || uploading ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <LinkedNoteDialog
        open={showNote}
        onClose={() => setShowNote(false)}
        onSaved={async () => {
          setShowNote(false);
          const savedId = editing?.id;
          if (savedId) {
            try {
              const existingData = buildData();
              await api.put(`/breeding-seasons/events/${savedId}`, { data: { ...existingData, has_note: true } });
            } catch { /* non-fatal */ }
          }
        }}
        horse={{ id: horseId }}
        careEntryRef={noteRef}
      />

      <AttachmentPickerSheet
        open={attachPickerOpen}
        onClose={() => setAttachPickerOpen(false)}
        onFiles={(files) => {
          setAttachments(prev => [...prev, ...files.map(f => ({ name: f.name, file: f }))]);
        }}
      />

      {/* Item 3: Sold date popup dialog */}
      <Dialog open={soldDialogOpen} onOpenChange={v => !v && setSoldDialogOpen(false)}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Confirm Sale Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Select the date the horse was sold to close this pregnancy record.</p>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Sale Date</Label>
              <Input ref={soldDateRef} type="date" value={soldDate}
                onChange={e => setSoldDate(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setSoldDialogOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-gray-700 hover:bg-gray-800 text-white"
              disabled={saving || !soldDate}
              onClick={async () => { setSoldDialogOpen(false); await handleResolve('Sold', { sold_date: soldDate }); }}>
              Confirm Sale
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Issue 4: Keep in care? dialog — FarmFolio tenants before marking pregnancy as Sold */}
      <Dialog open={keepInCareOpen} onOpenChange={setKeepInCareOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Keep in Care?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This horse is being marked as sold. Should it remain in your care (FarmFolio) and keep its active pregnancy records?
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={async () => {
              setKeepInCareOpen(false);
              // "No" — proceed with Sold resolution (closes pregnancy)
              setSoldDialogOpen(true);
            }}>No — remove from care</Button>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-white"
              onClick={async () => {
                setKeepInCareOpen(false);
                // "Yes" — mark sold with keep_in_care so automations are not paused
                await handleResolve('Sold', { sold_date: soldDate, keep_in_care: true });
              }}>Yes — keep in care</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
